import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdEdit, MdDelete, MdAddCircle } from 'react-icons/md';

export default function Articles() {
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [articles, setArticles] = useState([]);
  const [tab, setTab] = useState('all');
  const [filters, setFilters] = useState({ category: '', city: '', status: '' });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchArticles = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (tab === 'archive') {
        const res = await api.get('/admin/articles/archive', { params });
        setArticles(res.data.data);
        setPagination(res.data.pagination);
      } else {
        if (filters.category) params.category = filters.category;
        if (filters.city) params.city = filters.city;
        if (filters.status) params.status = filters.status;
        const res = await api.get('/admin/articles', { params });
        setArticles(res.data.data);
        setPagination(res.data.pagination);
      }
    } catch (err) {
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  }, [tab, filters]);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  useEffect(() => {
    api.get('/categories').then(res => setCategories(res.data.data)).catch(() => {});
    api.get('/cities').then(res => setCities(res.data.data)).catch(() => {});
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      await api.delete(`/admin/articles/${id}`);
      toast.success('Article deleted');
      fetchArticles(pagination.page);
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const startEdit = (article) => {
    setEditingId(article.id);
    setEditForm({ title: article.title, summary: article.summary, category: article.category, city: article.city, source: article.source });
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/admin/articles/${id}`, editForm);
      toast.success('Article updated');
      setEditingId(null);
      fetchArticles(pagination.page);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    }
  };

  const isLive = (article) => !article.is_archived && new Date(article.expires_at) > new Date();

  return (
    <div className="page">
      <div className="page-header">
        <h2>Articles</h2>
        <Link to="/articles/new" className="btn btn-primary"><MdAddCircle /> New Article</Link>
      </div>

      <div className="tabs">
        <button className={`tab ${tab === 'all' ? 'active' : ''}`} onClick={() => setTab('all')}>All Articles</button>
        <button className={`tab ${tab === 'archive' ? 'active' : ''}`} onClick={() => setTab('archive')}>Archive</button>
      </div>

      {tab === 'all' && (
        <div className="filter-bar">
          <select value={filters.category} onChange={(e) => setFilters(f => ({ ...f, category: e.target.value }))}>
            <option value="">All Categories</option>
            {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
          <select value={filters.city} onChange={(e) => setFilters(f => ({ ...f, city: e.target.value }))}>
            <option value="">All Cities</option>
            {cities.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
          <select value={filters.status} onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}>
            <option value="">All Status</option>
            <option value="live">Live</option>
            <option value="expired">Expired</option>
          </select>
        </div>
      )}

      {loading ? <div className="page-loader">Loading...</div> : (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th className="hide-mobile">Category</th>
                  <th className="hide-mobile">City</th>
                  <th className="hide-mobile">Views</th>
                  <th className="hide-mobile">Likes</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {articles.map(a => (
                  <tr key={a.id}>
                    <td className="title-cell">
                      {editingId === a.id ? (
                        <input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} className="edit-input" />
                      ) : (
                        <span className="article-title">{a.title}</span>
                      )}
                    </td>
                    <td className="hide-mobile">
                      <span className="badge badge-category">{a.category?.replace('_', ' ')}</span>
                    </td>
                    <td className="hide-mobile">
                      <span className="badge">{a.city?.replace('_', ' ')}</span>
                    </td>
                    <td className="hide-mobile">{a.view_count}</td>
                    <td className="hide-mobile">{a.like_count}</td>
                    <td>
                      <span className={`badge ${isLive(a) ? 'badge-live' : 'badge-expired'}`}>
                        {isLive(a) ? 'Live' : 'Expired'}
                      </span>
                    </td>
                    <td>
                      {editingId === a.id ? (
                        <div className="action-btns">
                          <button className="btn btn-sm btn-primary" onClick={() => saveEdit(a.id)}>Save</button>
                          <button className="btn btn-sm btn-ghost" onClick={() => setEditingId(null)}>Cancel</button>
                        </div>
                      ) : (
                        <div className="action-btns">
                          {isLive(a) && <button className="btn btn-sm btn-ghost" onClick={() => startEdit(a)}><MdEdit /></button>}
                          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(a.id)}><MdDelete /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {articles.length === 0 && (
                  <tr><td colSpan="7" className="empty-row">No articles found</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="mobile-cards">
            {articles.map(a => (
              <div key={a.id} className="mobile-card">
                <div className="mobile-card-header">
                  <span className={`badge ${isLive(a) ? 'badge-live' : 'badge-expired'}`}>
                    {isLive(a) ? 'Live' : 'Expired'}
                  </span>
                  <span className="badge badge-category">{a.category?.replace('_', ' ')}</span>
                </div>
                <h4>{a.title}</h4>
                <div className="mobile-card-stats">
                  <span>{a.view_count} views</span>
                  <span>{a.like_count} likes</span>
                  <span>{a.city?.replace('_', ' ')}</span>
                </div>
                <div className="action-btns">
                  {isLive(a) && <button className="btn btn-sm btn-ghost" onClick={() => startEdit(a)}><MdEdit /> Edit</button>}
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(a.id)}><MdDelete /> Delete</button>
                </div>
              </div>
            ))}
          </div>

          {pagination.pages > 1 && (
            <div className="pagination">
              {Array.from({ length: pagination.pages }, (_, i) => (
                <button key={i} className={`page-btn ${pagination.page === i + 1 ? 'active' : ''}`}
                  onClick={() => fetchArticles(i + 1)}>
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
